import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Calendar, GripVertical, Plus, Save, Trash2 } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDate, formatDateTime } from "@/lib/i18n";

interface SeriesSettings {
  schedulePattern?: string | null;
  requireSequentialProgress?: boolean;
  certificateEnabled?: boolean;
}

interface WebinarSeriesSummary {
  id: string;
  title: string;
  description: string | null;
  slug: string;
  settings: SeriesSettings;
  episodeCount: number;
  nextScheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SeriesEpisode {
  id: string;
  title: string;
  slug: string;
  status: string;
  type: string;
  scheduledAt: string | null;
  seriesId: string | null;
  seriesOrder: number | null;
}

interface WebinarSeriesDetail extends WebinarSeriesSummary {
  episodes: SeriesEpisode[];
  nextSeriesOrder: number;
}

interface SeriesEditorState {
  title: string;
  description: string;
  schedulePattern: string;
}

interface EpisodeDraft {
  title: string;
  type: "live" | "evergreen" | "hybrid";
  scheduledAt: string;
  timezone: string;
}

const DEFAULT_EPISODE_DRAFT = (timezone: string): EpisodeDraft => ({
  title: "",
  type: "live",
  scheduledAt: "",
  timezone,
});

export default function SeriesManagementPage() {
  const navigate = useNavigate();
  const { seriesId } = useParams();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [seriesList, setSeriesList] = useState<WebinarSeriesSummary[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<WebinarSeriesDetail | null>(null);
  const [editorState, setEditorState] = useState<SeriesEditorState>({ title: "", description: "", schedulePattern: "" });
  const [episodeDraft, setEpisodeDraft] = useState<EpisodeDraft>(DEFAULT_EPISODE_DRAFT(browserTimezone));
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggingEpisodeId, setDraggingEpisodeId] = useState<string | null>(null);

  const timezoneOptions = useMemo(() => {
    const base = ["UTC", browserTimezone, "Europe/Bucharest", "Europe/London", "America/New_York", "America/Los_Angeles"];
    return Array.from(new Set(base.filter(Boolean)));
  }, [browserTimezone]);

  useEffect(() => {
    void loadSeriesList();
  }, []);

  useEffect(() => {
    if (!seriesId) {
      setSelectedSeries(null);
      setEditorState({ title: "", description: "", schedulePattern: "" });
      return;
    }

    void loadSeriesDetail(seriesId);
  }, [seriesId]);

  async function loadSeriesList() {
    setIsLoadingList(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: WebinarSeriesSummary[] }>("/v1/webinars/series");
      setSeriesList(response.data);
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to load webinar series"));
    } finally {
      setIsLoadingList(false);
    }
  }

  async function loadSeriesDetail(targetSeriesId: string) {
    setIsLoadingDetail(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: WebinarSeriesDetail }>(`/v1/webinars/series/${targetSeriesId}`);
      setSelectedSeries(response.data);
      setEditorState({
        title: response.data.title,
        description: response.data.description ?? "",
        schedulePattern: response.data.settings.schedulePattern ?? "",
      });
      setEpisodeDraft(DEFAULT_EPISODE_DRAFT(browserTimezone));
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to load series detail"));
    } finally {
      setIsLoadingDetail(false);
    }
  }

  async function handleCreateSeries(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const title = String(formData.get("title") ?? "").trim();
    const description = String(formData.get("description") ?? "").trim();
    const schedulePattern = String(formData.get("schedulePattern") ?? "").trim();

    setIsSaving(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: WebinarSeriesSummary }>("/v1/webinars/series", {
        method: "POST",
        body: JSON.stringify({
          title,
          description: description || undefined,
          settings: {
            schedulePattern: schedulePattern || null,
          },
        }),
      });
      await loadSeriesList();
      setIsCreateOpen(false);
      navigate(`/webinars/series/${response.data.id}`);
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to create series"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveSeries() {
    if (!selectedSeries) return;

    setIsSaving(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: WebinarSeriesDetail }>(`/v1/webinars/series/${selectedSeries.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: editorState.title.trim(),
          description: editorState.description.trim() || null,
          settings: {
            ...selectedSeries.settings,
            schedulePattern: editorState.schedulePattern.trim() || null,
          },
        }),
      });
      setSelectedSeries(response.data);
      await loadSeriesList();
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to save series"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteSeries() {
    if (!selectedSeries) return;

    setIsSaving(true);
    setError(null);
    try {
      await apiFetch<{ message: string }>(`/v1/webinars/series/${selectedSeries.id}`, {
        method: "DELETE",
      });
      setSelectedSeries(null);
      await loadSeriesList();
      navigate("/webinars/series");
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to delete series"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReorderEpisodes(orderedWebinarIds: string[]) {
    if (!selectedSeries) return;

    setIsSaving(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: { episodes: SeriesEpisode[] } }>(`/v1/webinars/series/${selectedSeries.id}/reorder`, {
        method: "POST",
        body: JSON.stringify({ orderedWebinarIds }),
      });
      setSelectedSeries({
        ...selectedSeries,
        episodes: response.data.episodes,
      });
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to reorder episodes"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddEpisode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedSeries) return;

    setIsSaving(true);
    setError(null);
    try {
      await apiFetch<{ data: { id: string } }>("/v1/webinars", {
        method: "POST",
        body: JSON.stringify({
          title: episodeDraft.title.trim(),
          type: episodeDraft.type,
          timezone: episodeDraft.timezone,
          scheduledAt: episodeDraft.scheduledAt ? convertZonedLocalToUtcIso(episodeDraft.scheduledAt, episodeDraft.timezone) : undefined,
          registrationEnabled: true,
          seriesId: selectedSeries.id,
          seriesOrder: selectedSeries.nextSeriesOrder,
        }),
      });
      await loadSeriesDetail(selectedSeries.id);
      await loadSeriesList();
    } catch (caughtError) {
      setError(resolveErrorMessage(caughtError, "Unable to add episode"));
    } finally {
      setIsSaving(false);
    }
  }

  function moveEpisode(episodeId: string, direction: -1 | 1) {
    if (!selectedSeries) return;

    const index = selectedSeries.episodes.findIndex((episode) => episode.id === episodeId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= selectedSeries.episodes.length) {
      return;
    }

    const nextEpisodes = [...selectedSeries.episodes];
    const [moved] = nextEpisodes.splice(index, 1);
    if (!moved) {
      return;
    }
    nextEpisodes.splice(targetIndex, 0, moved);
    void handleReorderEpisodes(nextEpisodes.map((episode) => episode.id));
  }

  function handleDropEpisode(targetEpisodeId: string) {
    if (!selectedSeries || !draggingEpisodeId || draggingEpisodeId === targetEpisodeId) {
      setDraggingEpisodeId(null);
      return;
    }

    const nextEpisodes = [...selectedSeries.episodes];
    const currentIndex = nextEpisodes.findIndex((episode) => episode.id === draggingEpisodeId);
    const targetIndex = nextEpisodes.findIndex((episode) => episode.id === targetEpisodeId);
    if (currentIndex < 0 || targetIndex < 0) {
      setDraggingEpisodeId(null);
      return;
    }

    const [moved] = nextEpisodes.splice(currentIndex, 1);
    if (!moved) {
      setDraggingEpisodeId(null);
      return;
    }
    nextEpisodes.splice(targetIndex, 0, moved);
    setDraggingEpisodeId(null);
    void handleReorderEpisodes(nextEpisodes.map((episode) => episode.id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Webinar Series</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Group webinars into ordered programs, maintain the episode sequence, and launch linked webinars from one place.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)]"
        >
          <Plus className="h-4 w-4" />
          New Series
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Series Library</h2>
            <span className="text-xs text-[var(--color-muted-foreground)]">{seriesList.length} total</span>
          </div>

          {isLoadingList ? (
            <div className="space-y-3 text-sm text-[var(--color-muted-foreground)]">Loading webinar series…</div>
          ) : seriesList.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-4 py-8 text-sm text-[var(--color-muted-foreground)]">
              No webinar series yet. Create the first sequence and start attaching episodes.
            </div>
          ) : (
            <div className="space-y-3">
              {seriesList.map((series) => (
                <Link
                  key={series.id}
                  to={`/webinars/series/${series.id}`}
                  className={`block rounded-xl border px-4 py-3 transition-colors ${
                    series.id === seriesId
                      ? "border-[var(--color-primary-border)] bg-[var(--color-primary-light)]"
                      : "border-[var(--color-border)] bg-[var(--color-background)] hover:border-[var(--color-primary-border)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-[var(--color-foreground)]">{series.title}</div>
                      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{series.episodeCount} episodes</div>
                    </div>
                    {series.nextScheduledAt ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[var(--color-muted-foreground)]">
                        <Calendar className="h-3 w-3" />
                        {formatDate(series.nextScheduledAt)}
                      </span>
                    ) : null}
                  </div>
                  {series.settings.schedulePattern ? (
                    <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">{series.settings.schedulePattern}</div>
                  ) : null}
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
          {!seriesId ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-6 py-12 text-sm text-[var(--color-muted-foreground)]">
              Select a series from the library or create a new one to manage ordered webinar episodes.
            </div>
          ) : isLoadingDetail ? (
            <div className="text-sm text-[var(--color-muted-foreground)]">Loading series detail…</div>
          ) : !selectedSeries ? (
            <div className="text-sm text-[var(--color-muted-foreground)]">Series not found.</div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-[var(--color-foreground)]">Title</span>
                  <input
                    value={editorState.title}
                    onChange={(event) => setEditorState((current) => ({ ...current, title: event.target.value }))}
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-[var(--color-foreground)]">Schedule Pattern</span>
                  <input
                    value={editorState.schedulePattern}
                    onChange={(event) => setEditorState((current) => ({ ...current, schedulePattern: event.target.value }))}
                    placeholder="Weekly on Tuesday at 18:00"
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                  />
                </label>
              </div>

              <label className="grid gap-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Description</span>
                <textarea
                  value={editorState.description}
                  onChange={(event) => setEditorState((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                />
              </label>

              <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  {selectedSeries.episodeCount} episodes
                  {selectedSeries.nextScheduledAt ? ` · next session ${formatDateTime(selectedSeries.nextScheduledAt)}` : " · no scheduled session yet"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSaveSeries()}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" />
                    Save Series
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteSeries()}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 px-4 py-2 text-sm text-[var(--color-destructive)] disabled:opacity-60"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Ordered Episodes</h2>
                    <span className="text-xs text-[var(--color-muted-foreground)]">Drag or use arrows to reorder</span>
                  </div>

                  {selectedSeries.episodes.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-4 py-8 text-sm text-[var(--color-muted-foreground)]">
                      No episodes yet. Add the first webinar in this sequence.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedSeries.episodes.map((episode, index) => (
                        <div
                          key={episode.id}
                          draggable
                          onDragStart={() => setDraggingEpisodeId(episode.id)}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => handleDropEpisode(episode.id)}
                          className="flex items-center justify-between gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <GripVertical className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                            <div>
                              <div className="text-sm font-medium text-[var(--color-foreground)]">{episode.title}</div>
                              <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                                Episode {index + 1}
                                {episode.scheduledAt ? ` · ${formatDateTime(episode.scheduledAt)}` : " · unscheduled"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => moveEpisode(episode.id, -1)}
                              disabled={isSaving || index === 0}
                              className="rounded-lg border border-[var(--color-border)] px-3 py-1 text-xs disabled:opacity-40"
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              onClick={() => moveEpisode(episode.id, 1)}
                              disabled={isSaving || index === selectedSeries.episodes.length - 1}
                              className="rounded-lg border border-[var(--color-border)] px-3 py-1 text-xs disabled:opacity-40"
                            >
                              Down
                            </button>
                            <Link
                              to={`/webinars/${episode.id}/control`}
                              className="rounded-lg border border-[var(--color-border)] px-3 py-1 text-xs"
                            >
                              Open
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <form onSubmit={(event) => void handleAddEpisode(event)} className="space-y-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div>
                    <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Add Episode</h2>
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                      New webinars created here inherit the selected series and the next available order index.
                    </p>
                  </div>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Title</span>
                    <input
                      value={episodeDraft.title}
                      onChange={(event) => setEpisodeDraft((current) => ({ ...current, title: event.target.value }))}
                      placeholder="Episode title"
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm"
                    />
                  </label>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Type</span>
                    <select
                      value={episodeDraft.type}
                      onChange={(event) => setEpisodeDraft((current) => ({ ...current, type: event.target.value as EpisodeDraft["type"] }))}
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm"
                    >
                      <option value="live">Live</option>
                      <option value="evergreen">Evergreen</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </label>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Scheduled date & time</span>
                    <input
                      type="datetime-local"
                      value={episodeDraft.scheduledAt}
                      onChange={(event) => setEpisodeDraft((current) => ({ ...current, scheduledAt: event.target.value }))}
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm"
                    />
                  </label>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Timezone</span>
                    <select
                      value={episodeDraft.timezone}
                      onChange={(event) => setEpisodeDraft((current) => ({ ...current, timezone: event.target.value }))}
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm"
                    >
                      {timezoneOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="submit"
                    disabled={isSaving || episodeDraft.title.trim().length === 0}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
                  >
                    <Plus className="h-4 w-4" />
                    Add Episode
                  </button>
                </form>
              </div>
            </div>
          )}
        </section>
      </div>

      {isCreateOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setIsCreateOpen(false)}>
          <div
            role="dialog"
            aria-label="Create webinar series"
            className="w-full max-w-xl rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Create Webinar Series</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Define the sequence container first, then attach webinars as ordered episodes.
                </p>
              </div>
              <button type="button" onClick={() => setIsCreateOpen(false)} className="text-sm text-[var(--color-muted-foreground)]">
                Close
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={(event) => void handleCreateSeries(event)}>
              <label className="grid gap-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Title</span>
                <input
                  required
                  name="title"
                  placeholder="Customer Success Sprint"
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                />
              </label>

              <label className="grid gap-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Description</span>
                <textarea
                  name="description"
                  rows={4}
                  placeholder="Describe the promise, progression, or certification path."
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                />
              </label>

              <label className="grid gap-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Schedule Pattern</span>
                <input
                  name="schedulePattern"
                  placeholder="Weekly on Tuesday at 18:00"
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm"
                />
              </label>

              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => setIsCreateOpen(false)} className="rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
                >
                  {isSaving ? "Creating..." : "Create Series"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function resolveErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return `${error.status}: ${error.message}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function parseLocalDateTime(localDateTime: string): ZonedDateParts {
  const match = localDateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    throw new Error("Invalid local datetime");
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

function getZonedDateParts(date: Date, timeZone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const values: Partial<Record<keyof ZonedDateParts, number>> = {};
  for (const part of formatter.formatToParts(date)) {
    switch (part.type) {
      case "year":
      case "month":
      case "day":
      case "hour":
      case "minute":
      case "second":
        values[part.type] = Number(part.value);
        break;
      default:
        break;
    }
  }

  if (
    values.year === undefined
    || values.month === undefined
    || values.day === undefined
    || values.hour === undefined
    || values.minute === undefined
    || values.second === undefined
  ) {
    throw new Error(`Unable to resolve timezone parts for ${timeZone}`);
  }

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  };
}

function getOffsetMilliseconds(date: Date, timeZone: string): number {
  const zoned = getZonedDateParts(date, timeZone);
  const utcFromZoned = Date.UTC(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second,
  );

  return utcFromZoned - date.getTime();
}

function convertZonedLocalToUtcIso(localDateTime: string, timeZone: string): string {
  const parts = parseLocalDateTime(localDateTime);
  const initialUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const initialDate = new Date(initialUtc);
  const offset = getOffsetMilliseconds(initialDate, timeZone);
  const utcDate = new Date(initialUtc - offset);

  const correctedOffset = getOffsetMilliseconds(utcDate, timeZone);
  if (correctedOffset !== offset) {
    return new Date(initialUtc - correctedOffset).toISOString();
  }

  return utcDate.toISOString();
}
